import { useId, useMemo, useState, type ReactNode } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { clashKey, type ClashResult } from '@viewer/clash/lib'
import ClashResultsList, { type ClashResultsListProps } from './ClashResultsList'
import { linhasDosGrupos, patchDoLote, type LinhaDeGrupo, type LoteDoGrupo } from './clashPorGrupos'
import {
  ESPECIALIDADES,
  PRIORITIES,
  PRIORITY_LABELS,
  sugestoesDeEspecialidade,
  TRIAGE_LABELS,
  type ClashPriority,
  type ClashTriagePatch,
  type ClashTriageState,
} from './clashTriage'

// ---------------------------------------------------------------------------
// A lista "Por grupos" (clashPorGrupos.ts): uma linha por grupo, com o
// titulo, a prioridade, as interferencias, o estado agregado e o
// responsavel. Abrir o grupo mostra as accoes em lote (estado,
// prioridade, responsavel, recomendacao, nota), que passam para todos os
// conflitos do grupo nesta lista, e os conflitos dele. Os conflitos sem
// grupo ficam numa linha no fim.
// ---------------------------------------------------------------------------

const CAMPO =
  'rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-1 py-0.5 text-[11px]'
const BOTAO =
  'rounded border border-[var(--color-border)] px-1.5 py-0.5 text-[11px] disabled:opacity-40'

const PRIORITY_STYLE: Record<ClashPriority, string> = {
  critica: 'text-[var(--color-error-text)]',
  alta: 'text-[var(--color-warn-text)]',
  media: 'text-[var(--color-text-muted)]',
  baixa: 'text-[var(--color-text-dim)]',
}

const ESTADOS_DO_LOTE: ClashTriageState[] = ['novo', 'aceite', 'resolvido']

/** "Novo", ou "3 aceites, 2 novos" quando os estados diferem. */
function textoDoEstado(l: LinhaDeGrupo): string {
  const partes = Object.entries(l.estados) as [ClashTriageState, number][]
  if (partes.length === 1) return TRIAGE_LABELS[partes[0][0]]
  return partes
    .sort((a, b) => b[1] - a[1])
    .map(([s, n]) => `${n} ${TRIAGE_LABELS[s].toLowerCase()}${n === 1 ? '' : 's'}`)
    .join(', ')
}

type PropsDaLista = Omit<ClashResultsListProps, 'results' | 'groupBy'>

export interface ClashGruposListaProps extends PropsDaLista {
  results: ClashResult[]
  /** Grupos gravados (titulos, quando a triagem nao os tem). */
  grupos: Readonly<Record<string, { titulo?: string }>>
  /** Accao em lote: o mesmo patch em todos os conflitos indicados. */
  onLote: (keys: string[], patch: ClashTriagePatch) => void
  /** O que o grupo tem no BCF do modulo ("tópico aberto", "resolvido",
   *  "sem tópico"), quando a triagem ja foi para um BCF. */
  estadoNoBcf?: (chaves: readonly string[]) => string | null
}

function EditorDoLote({
  linha,
  modelos,
  onLote,
}: {
  linha: LinhaDeGrupo
  modelos: readonly string[]
  onLote: (keys: string[], patch: ClashTriagePatch) => void
}) {
  const id = useId()
  const [lote, setLote] = useState<LoteDoGrupo>({})
  const patch = patchDoLote(lote)
  const n = linha.chaves.length
  return (
    <div className="flex flex-col gap-1 border-b border-[var(--color-border)]/40 px-1 py-1" data-testid="clash-lote-grupo">
      <datalist id={`${id}-esp`}>
        {sugestoesDeEspecialidade(modelos).map((s) => (
          <option key={s} value={s}>
            {ESPECIALIDADES.find((e) => e.sigla === s)?.nome ?? s}
          </option>
        ))}
      </datalist>
      <div className="flex flex-wrap items-center gap-1">
        <select
          value={lote.state ?? ''}
          onChange={(e) => setLote({ ...lote, state: e.target.value as ClashTriageState | '' })}
          aria-label="Estado a aplicar ao grupo"
          className={CAMPO}
        >
          <option value="">estado</option>
          {ESTADOS_DO_LOTE.map((s) => (
            <option key={s} value={s}>
              {TRIAGE_LABELS[s]}
            </option>
          ))}
        </select>
        <select
          value={lote.priority ?? ''}
          onChange={(e) => setLote({ ...lote, priority: e.target.value as ClashPriority | '' })}
          aria-label="Prioridade a aplicar ao grupo"
          className={CAMPO}
        >
          <option value="">prioridade</option>
          {PRIORITIES.map((p) => (
            <option key={p} value={p}>
              {PRIORITY_LABELS[p]}
            </option>
          ))}
        </select>
        <input
          list={`${id}-esp`}
          value={lote.assignedTo ?? ''}
          onChange={(e) => setLote({ ...lote, assignedTo: e.target.value })}
          placeholder="responsável"
          aria-label="Especialidade responsável a aplicar ao grupo"
          className={`${CAMPO} w-28`}
        />
      </div>
      <textarea
        value={lote.recommendation ?? ''}
        onChange={(e) => setLote({ ...lote, recommendation: e.target.value })}
        placeholder="recomendação (substitui a de cada conflito)"
        aria-label="Recomendação a aplicar ao grupo"
        rows={2}
        className={CAMPO}
      />
      <input
        value={lote.note ?? ''}
        onChange={(e) => setLote({ ...lote, note: e.target.value })}
        placeholder="nota (substitui a de cada conflito)"
        aria-label="Nota a aplicar ao grupo"
        className={CAMPO}
      />
      <div>
        <button
          type="button"
          disabled={!patch}
          onClick={() => {
            if (!patch) return
            onLote(linha.chaves, patch)
            setLote({})
          }}
          className={BOTAO}
          title="Os campos preenchidos passam para todos os conflitos do grupo nesta lista; os vazios não mexem"
        >
          Aplicar {n === 1 ? 'ao conflito' : `aos ${n} conflitos`} do grupo
        </button>
      </div>
    </div>
  )
}

export default function ClashGruposLista({ results, grupos, onLote, estadoNoBcf, triage, checked, onSetChecked, modelos = [], ...lista }: ClashGruposListaProps) {
  const { linhas, semGrupo } = useMemo(() => linhasDosGrupos(results, triage, grupos), [results, triage, grupos])
  const [abertos, setAbertos] = useState<Set<string>>(new Set())
  const alternar = (id: string) =>
    setAbertos((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  if (results.length === 0) {
    return (
      <p className="px-3 py-3 text-[11px] italic text-[var(--color-text-dim)]">
        Nenhum conflito nesta lista (vejam-se os filtros e as regras de exclusão).
      </p>
    )
  }

  const linhaDaLista = (id: string, cabeca: ReactNode, chaves: string[], corpo: ReactNode) => {
    const aberto = abertos.has(id)
    const marcados = chaves.filter((k) => checked.has(k)).length
    return (
      <div key={id} className="mb-1 rounded border border-[var(--color-border)]/40">
        <div className="flex items-center gap-1 px-1 py-0.5">
          {onSetChecked && (
            <input
              type="checkbox"
              checked={marcados === chaves.length && chaves.length > 0}
              ref={(el) => {
                if (el) el.indeterminate = marcados > 0 && marcados < chaves.length
              }}
              onChange={() => onSetChecked(chaves, marcados < chaves.length)}
              title="Marcar o grupo inteiro (para exportar ou para a edição em lote dos marcados)"
              aria-label="Marcar o grupo"
              className="accent-[var(--color-accent)]"
            />
          )}
          <button
            type="button"
            onClick={() => alternar(id)}
            aria-expanded={aberto}
            className="flex min-w-0 flex-1 items-start gap-1 text-left text-[11px] hover:bg-[var(--color-bg)]"
          >
            <span className="mt-0.5 shrink-0">{aberto ? <ChevronDown size={12} /> : <ChevronRight size={12} />}</span>
            {cabeca}
          </button>
        </div>
        {aberto && corpo}
      </div>
    )
  }

  return (
    <div className="min-h-0 flex-1 overflow-y-auto px-2 py-1" data-testid="clash-lista-grupos">
      {linhas.map((l) =>
        linhaDaLista(
          l.id,
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="truncate text-[var(--color-text)]" title={l.titulo}>
              {l.prioridade && <span className={PRIORITY_STYLE[l.prioridade]}>{PRIORITY_LABELS[l.prioridade]} · </span>}
              {l.titulo}
            </span>
            <span className="text-[10px] text-[var(--color-text-dim)]">
              {l.results.length} interferência{l.results.length === 1 ? '' : 's'} · {textoDoEstado(l)}
              {l.responsavel ? ` · ${l.responsavel}` : ''}
              {l.niveis.length > 0 ? ` · ${l.niveis.join(', ')}` : ''}
              {(() => {
                const b = estadoNoBcf?.(l.chaves)
                return b ? ` · BCF: ${b}` : ''
              })()}
            </span>
          </span>,
          l.chaves,
          <>
            <EditorDoLote linha={l} modelos={modelos} onLote={onLote} />
            <ClashResultsList
              {...lista}
              results={l.results}
              groupBy="severity"
              triage={triage}
              checked={checked}
              onSetChecked={onSetChecked}
              modelos={modelos}
            />
          </>,
        ),
      )}
      {semGrupo.length > 0 &&
        linhaDaLista(
          '__sem_grupo__',
          <span className="text-[var(--color-text-muted)]">
            Sem grupo ({semGrupo.length})
          </span>,
          semGrupo.map(clashKey),
          <ClashResultsList
            {...lista}
            results={semGrupo}
            groupBy="severity"
            triage={triage}
            checked={checked}
            onSetChecked={onSetChecked}
            modelos={modelos}
          />,
        )}
    </div>
  )
}
